/**
 * Hyperswitch Error Handling Utilities
 *
 * Maps Hyperswitch error codes to user-friendly messages and provides
 * utilities for extracting error details from SDK responses and redirect URLs.
 */

export interface HyperswitchError {
  code: string;
  message: string;
  userMessage: string;
  suggestion: string;
  isRetryable: boolean;
}

/**
 * Comprehensive mapping of Hyperswitch error codes to user-friendly messages.
 * Includes standard payment processor codes and Hyperswitch-specific codes.
 */
const errorCodeMap: Record<string, Omit<HyperswitchError, 'code' | 'message'>> = {
  // Card decline reasons
  insufficient_funds: {
    userMessage: 'Your card was declined due to insufficient funds.',
    suggestion: 'Please check your account balance or try a different payment method.',
    isRetryable: true
  },
  card_declined: {
    userMessage: 'Your card was declined by your bank.',
    suggestion: 'Please contact your bank or try a different card.',
    isRetryable: true
  },
  generic_decline: {
    userMessage: 'Your card was declined.',
    suggestion: 'Please try a different card or contact your bank.',
    isRetryable: true
  },
  do_not_honor: {
    userMessage: 'Your bank declined this transaction.',
    suggestion: 'Please contact your bank to authorize the payment or use a different card.',
    isRetryable: true
  },
  expired_card: {
    userMessage: 'The card you used has expired.',
    suggestion: 'Please update your card information or use a different card.',
    isRetryable: true
  },
  invalid_card_number: {
    userMessage: 'The card number entered is invalid.',
    suggestion: 'Please check the card number and try again.',
    isRetryable: true
  },
  invalid_cvc: {
    userMessage: 'The security code (CVC) entered is incorrect.',
    suggestion: 'Please check the 3 or 4 digit code on your card and try again.',
    isRetryable: true
  },
  incorrect_cvc: {
    userMessage: 'The security code (CVC) entered is incorrect.',
    suggestion: 'Please check the 3 or 4 digit code on your card and try again.',
    isRetryable: true
  },
  invalid_expiry_month: {
    userMessage: 'The expiration month is invalid.',
    suggestion: 'Please check the expiration date and try again.',
    isRetryable: true
  },
  invalid_expiry_year: {
    userMessage: 'The expiration year is invalid.',
    suggestion: 'Please check the expiration date and try again.',
    isRetryable: true
  },
  card_not_supported: {
    userMessage: 'This card type is not supported.',
    suggestion: 'Please try a different card (Visa, Mastercard, or American Express).',
    isRetryable: true
  },
  currency_not_supported: {
    userMessage: 'Your card does not support this currency.',
    suggestion: 'Please try a different card.',
    isRetryable: true
  },

  // Fraud/security related
  fraudulent: {
    userMessage: 'This transaction was flagged as potentially fraudulent.',
    suggestion: 'Please contact your bank or try a different payment method.',
    isRetryable: false
  },
  stolen_card: {
    userMessage: 'This card has been reported as lost or stolen.',
    suggestion: 'Please use a different payment method.',
    isRetryable: false
  },
  lost_card: {
    userMessage: 'This card has been reported as lost.',
    suggestion: 'Please use a different payment method.',
    isRetryable: false
  },
  pickup_card: {
    userMessage: 'This card cannot be used for online transactions.',
    suggestion: 'Please contact your bank or use a different card.',
    isRetryable: false
  },

  // Limit related
  card_velocity_exceeded: {
    userMessage: 'You have exceeded the number of allowed transactions.',
    suggestion: 'Please wait and try again later, or use a different card.',
    isRetryable: true
  },
  withdrawal_count_limit_exceeded: {
    userMessage: 'You have reached your daily transaction limit.',
    suggestion: 'Please try again tomorrow or use a different card.',
    isRetryable: true
  },

  // Duplicate transaction
  duplicate_transaction: {
    userMessage: 'This transaction has already been processed.',
    suggestion: 'Your card may have already been charged. Please check your bank statement before retrying.',
    isRetryable: false
  },

  // Processing errors
  processing_error: {
    userMessage: 'There was a temporary issue processing your payment.',
    suggestion: 'This is usually temporary. Please try again in a few moments.',
    isRetryable: true
  },
  issuer_not_available: {
    userMessage: 'Your bank is temporarily unavailable.',
    suggestion: 'Please try again in a few minutes.',
    isRetryable: true
  },
  try_again_later: {
    userMessage: 'The payment system is temporarily busy.',
    suggestion: 'Please wait a moment and try again.',
    isRetryable: true
  },

  // Network errors
  network_error: {
    userMessage: 'There was a connection issue while processing your payment.',
    suggestion: 'Please check your internet connection and try again.',
    isRetryable: true
  },
  timeout: {
    userMessage: 'The payment request timed out.',
    suggestion: 'Please check your connection and try again.',
    isRetryable: true
  },

  // 3DS/Authentication
  authentication_failed: {
    userMessage: 'Card authentication failed.',
    suggestion: 'Please try again and complete the verification step, or use a different card.',
    isRetryable: true
  },
  three_ds_failed: {
    userMessage: '3D Secure verification failed.',
    suggestion: 'Please try again and complete the bank verification, or use a different card.',
    isRetryable: true
  },

  // Hyperswitch specific
  hs_payment_failed: {
    userMessage: 'Payment could not be processed.',
    suggestion: 'Please try again or use a different payment method.',
    isRetryable: true
  },
  hs_processing_failed: {
    userMessage: 'Payment processing failed.',
    suggestion: 'Please try again in a few moments.',
    isRetryable: true
  },
  connector_error: {
    userMessage: 'There was an issue with the payment processor.',
    suggestion: 'Please try again or use a different payment method.',
    isRetryable: true
  },

  // User actions
  payment_cancelled: {
    userMessage: 'Payment was cancelled.',
    suggestion: 'You can try again when ready.',
    isRetryable: true
  },
  abandoned: {
    userMessage: 'Payment was not completed.',
    suggestion: 'Please try again to complete your payment.',
    isRetryable: true
  }
};

/**
 * Parse error details from Hyperswitch redirect URL parameters
 */
export function parseRedirectError(searchParams: URLSearchParams): HyperswitchError | null {
  const redirectStatus = searchParams.get('redirect_status');

  if (redirectStatus === 'succeeded') {
    return null;
  }

  const errorCode = searchParams.get('error_code') ||
                    searchParams.get('error') ||
                    searchParams.get('payment_intent_status') ||
                    redirectStatus ||
                    'unknown';

  const errorMessage = searchParams.get('error_message') ||
                       searchParams.get('message') ||
                       '';

  return mapHyperswitchError(errorCode, errorMessage);
}

/**
 * Parse error from Hyperswitch SDK error object
 */
export function parseSDKError(error: any): HyperswitchError {
  // Handle different error formats from the SDK
  const code = error?.code ||
               error?.error?.code ||
               error?.type ||
               error?.error?.type ||
               'unknown';

  const message = error?.message ||
                  error?.error?.message ||
                  (typeof error === 'string' ? error : '') ||
                  'An unexpected error occurred';

  return mapHyperswitchError(code, message);
}

/**
 * Map an error code and message to a user-friendly HyperswitchError
 */
export function mapHyperswitchError(code: string, message: string = ''): HyperswitchError {
  // Normalize the code for lookup
  const normalizedCode = code.toLowerCase().replace(/[\s-]+/g, '_');

  // Try to find a mapping
  const mapping = errorCodeMap[normalizedCode];

  if (mapping) {
    return {
      code,
      message: message || normalizedCode,
      ...mapping
    };
  }

  // Try to infer from the message if no direct mapping
  const inferredMapping = inferFromMessage(message);
  if (inferredMapping) {
    return {
      code,
      message,
      ...inferredMapping
    };
  }

  // Default fallback
  return {
    code,
    message: message || 'Payment failed',
    userMessage: message || 'Your payment could not be processed.',
    suggestion: 'Please try again or use a different payment method.',
    isRetryable: true
  };
}

/**
 * Try to infer error type from message content
 */
function inferFromMessage(message: string): Omit<HyperswitchError, 'code' | 'message'> | null {
  const lowerMessage = message.toLowerCase();

  if (lowerMessage.includes('insufficient') || lowerMessage.includes('balance')) {
    return errorCodeMap.insufficient_funds;
  }
  if (lowerMessage.includes('declined')) {
    return errorCodeMap.card_declined;
  }
  if (lowerMessage.includes('expired')) {
    return errorCodeMap.expired_card;
  }
  if (lowerMessage.includes('cvc') || lowerMessage.includes('cvv') || lowerMessage.includes('security code')) {
    return errorCodeMap.invalid_cvc;
  }
  if (lowerMessage.includes('network') || lowerMessage.includes('connection')) {
    return errorCodeMap.network_error;
  }
  if (lowerMessage.includes('timeout')) {
    return errorCodeMap.timeout;
  }
  if (lowerMessage.includes('authentication') || lowerMessage.includes('3ds') || lowerMessage.includes('3d secure')) {
    return errorCodeMap.authentication_failed;
  }
  if (lowerMessage.includes('cancel')) {
    return errorCodeMap.payment_cancelled;
  }
  if (lowerMessage.includes('fraud')) {
    return errorCodeMap.fraudulent;
  }
  if (lowerMessage.includes('duplicate')) {
    return errorCodeMap.duplicate_transaction;
  }

  return null;
}

/**
 * Format error for display in UI
 */
export function formatErrorForDisplay(error: HyperswitchError): {
  title: string;
  description: string;
  suggestion: string;
  errorCode: string | null;
  canRetry: boolean;
} {
  return {
    title: 'Payment Failed',
    description: error.userMessage,
    suggestion: error.suggestion,
    errorCode: error.code !== 'unknown' ? error.code : null,
    canRetry: error.isRetryable
  };
}

/**
 * Log error to console with full details for debugging
 */
export function logPaymentError(
  context: string,
  error: HyperswitchError,
  extra?: Record<string, any>
): void {
  console.error(`[Payment Error] ${context}:`, {
    code: error.code,
    message: error.message,
    userMessage: error.userMessage,
    isRetryable: error.isRetryable,
    ...extra
  });
}
